import {
  createTheme,
  filledInputClasses,
  inputLabelClasses,
  outlinedInputClasses,
  paperClasses,
  tableCellClasses,
} from "@mui/material";
const muiTheme = createTheme();

const appTheme = (
  backgroundColor: string,
  contrastTextColor: string,
  palette: any
) => {
  return {
    typography: {
      fontFamily: "Poppins",
      // fontFamily: ["Poppins", "Helvetica", "Arial", "sans-serif"].join(","),
      body1: {
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.57,
      },
      button: {
        fontWeight: 600,
      },
      caption: {
        fontSize: "0.75rem",
        fontWeight: 500,
        lineHeight: 1.66,
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.57,
      },
      subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.57,
      },
      overline: {
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.5px",
        lineHeight: 2.5,
        textTransform: "uppercase",
      },
      h1: {
        fontWeight: 700,
        fontSize: "45px",
        lineHeight: "42px",
        letterSpacing: "1px",
      },
      h2: {
        fontWeight: 700,
        fontSize: "3rem",
        lineHeight: 1.2,
        letterSpacing: "1px",
      },
      h3: {
        fontWeight: 700,
        fontSize: "2.25rem",
        lineHeight: 1.2,
        letterSpacing: "1px",
      },
      h4: {
        fontWeight: 700,
        fontSize: "2rem",
        lineHeight: 1.2,
        letterSpacing: "1px",
      },
      h5: {
        fontWeight: 700,
        fontSize: "1.5rem",
        lineHeight: 1.2,
        letterSpacing: "1px",
      },
      h6: {
        fontWeight: 700,
        fontSize: "1.125rem",
        lineHeight: 1.2,
        letterSpacing: "1px",
      },
    },

    spacing: 8,

    components: {
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: 0,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
            textTransform: "none",
            letterSpacing: "1px",
          },
          sizeSmall: {
            padding: "6px 16px",
          },
          sizeMedium: {
            height: "2.4rem",
            padding: "8px 20px",
          },
          sizeLarge: {
            height: "2.4rem",
            padding: "11px 24px",
          },
          textSizeSmall: {
            padding: "7px 12px",
          },
          textSizeMedium: {
            padding: "9px 16px",
          },
          textSizeLarge: {
            padding: "12px 16px",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            [`&.${paperClasses.elevation1}`]: {
              boxShadow:
                "0px 5px 22px rgba(0, 0, 0, 0.04), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.03)",
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            // padding: "32px 24px",
            "&:last-child": {
              // paddingBottom: "32px",
            },
          },
        },
      },
      MuiCardHeader: {
        defaultProps: {
          titleTypographyProps: {
            variant: "h6",
            fontSize: 12,
            fontWeight: "600",
            lineHeight: 1,
            letterSpacing: 0.5,
          },
          subheaderTypographyProps: {
            variant: "body2",
          },
        },
        styleOverrides: {
          root: {
            padding: "14px 24px 14px",
            backgroundColor: palette.neutral[200],
            color: palette.primary.dark,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            "--dhx-scheduler-base-colors-primary": `${palette.primary.main} !important`,
            "--dhx-scheduler-event-background": "#ffffff !important",
            "--dhx-scheduler-event-color": "#000000 !important",
            "--dhx-scheduler-container-background": "#f4f6f8 !important",
            "--dhx-scheduler-navline-font-color": "#555555 !important",
            // "--dhx-scheduler-container-color": "#ffffff !important",
            "--dhx-scheduler-base-colors-border": "#e0e0e0 !important",
            "--dhx-scheduler-font-size": "14px !important",
            "--dhx-scheduler-border-radius": "5px !important",
            "--dhx-scheduler-toolbar-height": "auto !important",
          },

          "*": {
            boxSizing: "border-box",
          },
          html: {
            MozOsxFontSmoothing: "grayscale",
            WebkitFontSmoothing: "antialiased",
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            width: "100%",
          },
          body: {
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            minHeight: "100%",
            width: "100%",
          },
          "#__next": {
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            height: "100%",
            width: "100%",
          },
          "#nprogress": {
            pointerEvents: "none",
          },
          "#nprogress .bar": {
            backgroundColor: palette.primary.main,
            height: 3,
            left: 0,
            position: "fixed",
            top: 0,
            width: "100%",
            zIndex: 2000,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              opacity: 1,
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          input: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "24px",
            "&::placeholder": {
              color: palette.secondary.main,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "transparent",
            borderRadius: 6,
            borderStyle: "solid",
            borderWidth: 1,
            overflow: "hidden",
            borderColor: palette.neutral[200],
            transition: muiTheme.transitions.create([
              "border-color",
              "box-shadow",
            ]),

            "&:before": {
              display: "none",
            },
            "&:after": {
              display: "none",
            },
            [`&.${filledInputClasses.disabled}`]: {
              backgroundColor: "transparent",
            },
            [`&.${filledInputClasses.focused}`]: {
              backgroundColor: "transparent",
              borderColor: palette.primary.main,
              boxShadow: `${palette.primary.main} 0 0 0 2px`,
            },
            [`&.${filledInputClasses.error}`]: {
              borderColor: palette.error.main,
              boxShadow: `${palette.error.main} 0 0 0 2px`,
            },
          },
          input: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "24px",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`&.${filledInputClasses.error}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: palette.error.main,
                boxShadow: `${palette.error.main} 0 0 0 2px`,
              },
            },
          },
          input: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "24px",
          },
          notchedOutline: {
            borderColor: palette.neutral[200],
            transition: muiTheme.transitions.create([
              "border-color",
              "box-shadow",
            ]),
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 14,
            fontWeight: 500,
            [`&.${inputLabelClasses.filled}`]: {
              transform: "translate(12px, 18px) scale(1)",
            },
            [`&.${inputLabelClasses.shrink}`]: {
              [`&.${inputLabelClasses.standard}`]: {
                transform: "translate(0, -1.5px) scale(0.85)",
              },
              [`&.${inputLabelClasses.filled}`]: {
                transform: "translate(12px, 6px) scale(0.85)",
              },
              [`&.${inputLabelClasses.outlined}`]: {
                transform: "translate(14px, -9px) scale(0.85)",
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.71,
            minWidth: "auto",
            paddingLeft: 0,
            paddingRight: 0,
            textTransform: "none",
            "& + &": {
              marginLeft: 24,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            //borderBottomColor: palette.divider,
            border: "none",
            padding: "5px 15px",
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            [`& .${tableCellClasses.root}:nth-of-type(2)`]: {
              fontWeight: 600,
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
            //boxShadow: "0 0 3px rgba(0, 0, 0, 0.2)",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            borderBottom: "none",
            backgroundColor: "#e2e2e2",
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
              backgroundColor: "#e2e2e2",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: palette.primary.main,
            },
            [`& .${tableCellClasses.paddingCheckbox}`]: {
              paddingTop: 4,
              paddingBottom: 4,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "filled",
        },
      },

      //   MuiAccordion: {
      //     styleOverrides: {
      //       root: {
      //         margin: "25px 0 !important",
      //       },
      //     },
      //   },
      //   MuiAccordionSummary: {
      //     styleOverrides: {
      //       root: {
      //         backgroundColor: "transparent",
      //         color: palette.primary.dark,
      //         borderColor: palette.primary.main,
      //         borderWidth: "1px",
      //         borderStyle: "solid",
      //         borderRadius: "6px",
      //         height: "50px",
      //         transition: "height 0.3s ease",
      //         "&.Mui-expanded": {
      //           height: "40px",
      //           borderRadius: "6px 6px 0 0",
      //         },
      //       },
      //       expandIconWrapper: {
      //         "& .MuiSvgIcon-root": {
      //           fill: palette.primary.main,
      //         },
      //       },
      //     },
      //   },
      //   MuiAccordionDetails: {
      //     styleOverrides: {
      //       root: {
      //         backgroundColor: "#fbfbfb",
      //         borderColor: palette.primary.main,
      //         borderWidth: "0 1px 1px 1px",
      //         borderStyle: "solid",
      //         padding: 24,
      //       },
      //     },
      //   },
      //   MuiDialogTitle: {
      //     styleOverrides: {
      //       root: {
      //         backgroundColor: palette.neutral[200],
      //         padding: "8px 16px",
      //       },
      //     },
      //   },
    },
  };
};

export default appTheme;
